'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { BaseModal } from './BaseModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { PaginationControls } from './PaginationControls';
import { Select, type SelectOption } from './Select';
import { Spinner } from './loading/Spinner';
import { Tooltip } from './Tooltip';
import {
  useCreateMemberRunnerModelMutation,
  useBulkUpdateMemberRunnerModelApprovalMutation,
  useDeleteMemberRunnerModelMutation,
  useMemberRunnerModels,
  useUpdateMemberRunnerModelMutation,
} from '../hooks/queries/useMemberRunnerModels';
import { useDaemons } from '../hooks/queries/useDaemons';
import { getRunnerEffortLevels, runnerTypeValues, type RunnerType } from '../lib/api';
import { getEffortLevelLabel } from '../lib/runnerEffort';
import { getRunnerTypeLabel } from './daemonTriggerDetailUtils';
import { isApiError } from '../lib/api/error';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import type { MemberRunnerModelRecord } from '../lib/api/memberRunnerModels';
import { cn } from '../lib/cn';

type ModalState = { mode: 'create' } | { mode: 'edit'; model: MemberRunnerModelRecord } | null;

type Props = {
  token: string | null;
};

type ModelListSection = 'approved' | 'pending';

// 탐지 모델은 머신 1대에 약 60건까지 나오므로 승인 대기와 승인 완료를 서버 필터로 나눠 각각 페이징한다.
// 한 목록에 섞으면 pageSize 하드캡(100) 밖으로 밀려난 항목을 승인도 거절도 할 수 없다.
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 20;

type ModelActionsMenuProps = {
  ariaLabel: string;
  editLabel: string;
  deleteLabel: string;
  deleteDisabled: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

const ModelActionsMenu = ({
  ariaLabel,
  editLabel,
  deleteLabel,
  deleteDisabled,
  onEdit,
  onDelete,
}: ModelActionsMenuProps) => {
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    strategy: 'fixed',
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  return (
    <Popover className="relative shrink-0">
      <PopoverButton ref={refs.setReference} className="btn btn-ghost btn-xs btn-circle" aria-label={ariaLabel}>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="12" cy="19" r="1.8" />
        </svg>
      </PopoverButton>
      <FloatingPortal>
        {/*
         * 이 목록은 항상 `ExecutionEnvironmentModal`(BaseModal) 안에서 뜨는데, 패널은 FloatingPortal로
         * document.body에 붙는다. BaseModal은 ModalStackProvider에서 z-index 1000부터 배정받으므로
         * 모달보다 낮은 값을 주면 패널이 모달 뒤에 그려져 "버튼을 눌러도 반응이 없는" 상태가 된다.
         * 모달 위에 떠야 하는 포털 요소의 상한인 z-[1040]을 쓴다(Select·EffortSlider·Tooltip과 같은 값).
         */}
        <PopoverPanel
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-[1040] w-28 rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
        >
          {({ close }) => (
            <ul className="menu p-0">
              <li>
                <button
                  type="button"
                  onClick={() => {
                    close();
                    onEdit();
                  }}
                >
                  {editLabel}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="text-error"
                  onClick={() => {
                    close();
                    onDelete();
                  }}
                  disabled={deleteDisabled}
                >
                  {deleteLabel}
                </button>
              </li>
            </ul>
          )}
        </PopoverPanel>
      </FloatingPortal>
    </Popover>
  );
};

// 라벨 없이 등록된 러너는 서버가 daemonLabels에서 걸러내므로, 러너 없음(연결 해제)과 구분해 id 앞자리로 표시한다.
const resolveMachineLabel = (
  model: MemberRunnerModelRecord,
  labels: { allMachines: string; unavailableMachine: string },
): string => {
  if (!model.machineScope) return labels.allMachines;
  const [firstLabel] = model.machineScope.daemonLabels;
  if (firstLabel) return firstLabel;
  const [firstDaemonId] = model.machineScope.daemonIds;
  return firstDaemonId ? firstDaemonId.slice(0, 8) : labels.unavailableMachine;
};

export const MemberRunnerModelManager = ({ token }: Props) => {
  const t = useTranslations('memberRunnerModels');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const tLayout = useTranslations('components.appLayout');

  const [modalState, setModalState] = useState<ModalState>(null);
  const [runnerType, setRunnerType] = useState<RunnerType>(runnerTypeValues[0]);
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [maxInputTokens, setMaxInputTokens] = useState('');
  const [fastModeSupported, setFastModeSupported] = useState(false);
  const [supportedEffortLevels, setSupportedEffortLevels] = useState<string[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<MemberRunnerModelRecord | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [activeList, setActiveList] = useState<ModelListSection>('approved');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [runnerFilter, setRunnerFilter] = useState<RunnerType | 'ALL'>('ALL');
  const [machineFilter, setMachineFilter] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isFilterSelection, setIsFilterSelection] = useState(false);
  const [bulkConfirmAction, setBulkConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const selectCurrentPageRef = useRef<HTMLInputElement>(null);

  const [pendingPage, setPendingPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [approvedPage, setApprovedPage] = useState(1);
  const [approvedPageSize, setApprovedPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const machineLabels = { allMachines: t('allMachines'), unavailableMachine: t('unavailableMachine') };
  const { data: daemonsResult } = useDaemons(token);
  const machineOptions: SelectOption[] = [
    { value: 'ALL', label: t('allRunners') },
    ...Array.from(
      new Map(
        (daemonsResult?.daemons ?? [])
          .filter((daemon) => daemon.machineId)
          .map((daemon) => [daemon.machineId as string, daemon.label || daemon.id.slice(0, 8)]),
      ).entries(),
    ).map(([machineId, machineLabel]) => ({ value: machineId, label: machineLabel })),
  ];

  const { data: pendingResult, isLoading: isPendingLoading } = useMemberRunnerModels(token, {
    pending: true,
    ...(search ? { search } : {}),
    ...(runnerFilter !== 'ALL' ? { runnerType: runnerFilter } : {}),
    ...(machineFilter !== 'ALL' ? { machineId: machineFilter } : {}),
    page: pendingPage,
    pageSize: pendingPageSize,
  });
  const { data: approvedResult, isLoading: isApprovedLoading } = useMemberRunnerModels(token, {
    pending: false,
    ...(search ? { search } : {}),
    ...(runnerFilter !== 'ALL' ? { runnerType: runnerFilter } : {}),
    ...(machineFilter !== 'ALL' ? { machineId: machineFilter } : {}),
    page: approvedPage,
    pageSize: approvedPageSize,
  });

  const pendingModels = pendingResult?.data ?? [];
  const approvedModels = approvedResult?.data ?? [];
  const pendingMeta = pendingResult?.meta;
  const approvedMeta = approvedResult?.meta;

  // 승인·거절로 마지막 페이지가 비면 목록이 통째로 사라져 보이므로 유효한 페이지로 되돌린다.
  useEffect(() => {
    if (pendingMeta && pendingPage > 1 && pendingPage > pendingMeta.totalPages) {
      setPendingPage(Math.max(pendingMeta.totalPages, 1));
    }
  }, [pendingMeta, pendingPage]);

  useEffect(() => {
    if (approvedMeta && approvedPage > 1 && approvedPage > approvedMeta.totalPages) {
      setApprovedPage(Math.max(approvedMeta.totalPages, 1));
    }
  }, [approvedMeta, approvedPage]);

  const createMutation = useCreateMemberRunnerModelMutation(token);
  const updateMutation = useUpdateMemberRunnerModelMutation(token);
  const bulkApprovalMutation = useBulkUpdateMemberRunnerModelApprovalMutation(token);
  const deleteMutation = useDeleteMemberRunnerModelMutation(token);

  const canEnableFastMode =
    runnerType === 'CODEX' || (runnerType === 'CLAUDE_CODE' && value.toLowerCase().includes('opus'));
  const availableEffortLevels = [...getRunnerEffortLevels(runnerType)];
  const canEnableEffort = availableEffortLevels.length > 0;

  const runnerTypeOptions: SelectOption[] = runnerTypeValues.map((rt) => ({
    value: rt,
    label: getRunnerTypeLabel(rt, tLayout),
  }));

  const createRunnerTypeOptions: SelectOption[] = runnerTypeValues.map((rt) => ({
    value: rt,
    label: getRunnerTypeLabel(rt, tLayout),
  }));

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPendingPage(1);
    setApprovedPage(1);
  }, [machineFilter, runnerFilter, search]);

  // 페이지 밖의 보이지 않는 선택을 처리하지 않도록 목록 맥락이 바뀌면 선택을 비운다.
  useEffect(() => {
    setSelectedIds([]);
    setIsFilterSelection(false);
  }, [
    activeList,
    approvedPage,
    approvedPageSize,
    machineFilter,
    pendingPage,
    pendingPageSize,
    runnerFilter,
    search,
    searchInput,
  ]);

  const selectedIdSet = new Set(selectedIds);
  const isCurrentPageSelected = pendingModels.length > 0 && pendingModels.every((model) => selectedIdSet.has(model.id));
  const isCurrentPagePartiallySelected =
    pendingModels.some((model) => selectedIdSet.has(model.id)) && !isCurrentPageSelected;

  useEffect(() => {
    if (selectCurrentPageRef.current) {
      selectCurrentPageRef.current.indeterminate = isCurrentPagePartiallySelected;
    }
  }, [isCurrentPagePartiallySelected]);

  // 러너 타입을 바꾸면 새 타입이 허용하지 않는 Effort 레벨을 선택 목록에서 제거한다.
  useEffect(() => {
    const allowed = getRunnerEffortLevels(runnerType);
    setSupportedEffortLevels((prev) => {
      const filtered = prev.filter((level) => allowed.includes(level));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [runnerType]);

  const openCreate = () => {
    setRunnerType(runnerTypeValues[0]);
    setLabel('');
    setValue('');
    setSortOrder('0');
    setMaxInputTokens('');
    setFastModeSupported(false);
    setSupportedEffortLevels([]);
    setModalState({ mode: 'create' });
  };

  const openEdit = (model: MemberRunnerModelRecord) => {
    setRunnerType(model.runnerType);
    setLabel(model.label);
    setValue(model.value);
    setSortOrder(String(model.sortOrder));
    setMaxInputTokens(model.maxInputTokens != null ? String(model.maxInputTokens) : '');
    setFastModeSupported(model.fastModeSupported);
    setSupportedEffortLevels(model.supportedEffortLevels ?? []);
    setModalState({ mode: 'edit', model });
  };

  const handleSave = async () => {
    const parsedMaxInputTokens = maxInputTokens.trim().length > 0 ? Number.parseInt(maxInputTokens, 10) : null;
    const resolvedMaxInputTokens = Number.isNaN(parsedMaxInputTokens) ? null : parsedMaxInputTokens;

    if (modalState?.mode === 'create') {
      try {
        await createMutation.mutateAsync({
          runnerType,
          label: label.trim(),
          value: value.trim(),
          sortOrder: Number(sortOrder) || 0,
          maxInputTokens: resolvedMaxInputTokens,
          fastModeSupported: canEnableFastMode && fastModeSupported,
          supportedEffortLevels: canEnableEffort
            ? supportedEffortLevels.filter((level) => availableEffortLevels.includes(level))
            : [],
        });
        toast.success(t('createSuccess'));
        setModalState(null);
      } catch (error) {
        const message = isApiError(error) ? resolveApiErrorToastMessage(tRoot, error) : t('createError');
        toast.error(message);
      }
    } else if (modalState?.mode === 'edit') {
      try {
        // 탐지 모델도 수동 모델과 같은 필드를 보낸다. 서버가 실제로 바뀐 값을 감지하면 그 행을
        // MANUAL로 승격시켜 다음 탐지 주기의 갱신에서 빼주므로, 클라이언트가 막을 필드는 없다.
        // source는 서버 판단이라 여기서 보내지 않는다.
        await updateMutation.mutateAsync({
          id: modalState.model.id,
          input: {
            label: label.trim(),
            value: value.trim(),
            sortOrder: Number(sortOrder) || 0,
            maxInputTokens: resolvedMaxInputTokens,
            fastModeSupported: canEnableFastMode && fastModeSupported,
            supportedEffortLevels: canEnableEffort
              ? supportedEffortLevels.filter((level) => availableEffortLevels.includes(level))
              : [],
          },
        });
        toast.success(t('updateSuccess'));
        setModalState(null);
      } catch (error) {
        const message = isApiError(error) ? resolveApiErrorToastMessage(tRoot, error) : t('updateError');
        toast.error(message);
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t('deleteSuccess'));
      setDeleteTarget(null);
    } catch (error) {
      const message = isApiError(error) ? resolveApiErrorToastMessage(tRoot, error) : t('deleteError');
      toast.error(message);
    }
  };

  // 거절은 삭제가 아니다. 행을 지우면 러너가 6시간 뒤 같은 모델을 다시 보고해 승인 대기로 되살아난다.
  const handleReject = async (model: MemberRunnerModelRecord) => {
    try {
      await updateMutation.mutateAsync({ id: model.id, input: { isRejected: true } });
      toast.success(t('rejectSuccess'));
    } catch (error) {
      const message = isApiError(error) ? resolveApiErrorToastMessage(tRoot, error) : t('rejectError');
      toast.error(message);
    }
  };

  const handleApprove = async (model: MemberRunnerModelRecord) => {
    try {
      await updateMutation.mutateAsync({ id: model.id, input: { isConfirmed: true } });
      toast.success(t('approveSuccess'));
    } catch (error) {
      const message = isApiError(error) ? resolveApiErrorToastMessage(tRoot, error) : t('approveError');
      toast.error(message);
    }
  };

  const toggleSelectedId = (id: string) => {
    setIsFilterSelection(false);
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id],
    );
  };

  const toggleCurrentPage = () => {
    setIsFilterSelection(false);
    setSelectedIds(isCurrentPageSelected ? [] : pendingModels.map((model) => model.id));
  };

  const approvalFilter = {
    pending: true,
    ...(search ? { search } : {}),
    ...(runnerFilter !== 'ALL' ? { runnerType: runnerFilter } : {}),
    ...(machineFilter !== 'ALL' ? { machineId: machineFilter } : {}),
  };

  const selectedCount = isFilterSelection ? (pendingMeta?.total ?? 0) : selectedIds.length;

  const handleBulkApproval = async (action: 'approve' | 'reject') => {
    if (selectedCount === 0) return;

    try {
      const result = await bulkApprovalMutation.mutateAsync(
        isFilterSelection ? { action, filter: approvalFilter } : { action, ids: selectedIds },
      );
      toast.success(t('bulkUpdateSuccess', { count: result.updated }));
      setSelectedIds([]);
      setIsFilterSelection(false);
      setBulkConfirmAction(null);
    } catch (error) {
      const message = isApiError(error) ? resolveApiErrorToastMessage(tRoot, error) : t('bulkUpdateError');
      toast.error(message);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending || bulkApprovalMutation.isPending;
  const isDetectedEdit = modalState?.mode === 'edit' && modalState.model.source === 'DETECTED';

  const editorModal = (
    <BaseModal isOpen={modalState !== null} onClose={() => setModalState(null)} variant="bottomSheet" size="lg">
      {modalState ? (
        <>
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold">
                {modalState.mode === 'create' ? t('addModel') : t('editModel')}
              </h3>
              <p className="mt-1 text-xs leading-5 text-[var(--color-fg-muted)]">{t('description')}</p>
            </div>
            <button
              type="button"
              aria-label="Close"
              className="btn btn-ghost btn-circle btn-sm"
              onClick={() => setModalState(null)}
              disabled={isSaving}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex min-w-0 flex-col">
                <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]">{t('runnerType')}</label>
                <Select
                  size="sm"
                  value={runnerType}
                  onChange={(val) => setRunnerType(val as RunnerType)}
                  options={modalState.mode === 'create' ? createRunnerTypeOptions : runnerTypeOptions}
                  disabled={modalState.mode === 'edit'}
                  fullWidth
                />
              </div>
              <div className="flex min-w-0 flex-col">
                <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]">{t('sortOrder')}</label>
                <input
                  className="input input-sm input-bordered w-full"
                  inputMode="numeric"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
              </div>
              <div className="flex min-w-0 flex-col">
                <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]">
                  {t('maxInputTokens')}
                </label>
                <input
                  className="input input-sm input-bordered w-full"
                  inputMode="numeric"
                  value={maxInputTokens}
                  onChange={(e) => setMaxInputTokens(e.target.value)}
                  placeholder={t('maxInputTokensPlaceholder')}
                />
              </div>
            </div>
            <div className="flex min-w-0 flex-col">
              <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]">{t('label')}</label>
              <input
                className="input input-sm input-bordered w-full"
                placeholder={t('labelPlaceholder')}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="flex min-w-0 flex-col">
              <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]">{t('value')}</label>
              <input
                className="input input-sm input-bordered w-full font-mono"
                placeholder={t('valuePlaceholder')}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
            {isDetectedEdit ? (
              <p className="rounded-lg bg-[var(--chip-info-bg)] px-3 py-2 text-xs leading-5 text-[var(--chip-info-fg)]">
                {t('detectedEditTakesOwnership')}
              </p>
            ) : null}
            {/* fastMode·Effort는 탐지 모델에서도 편집할 수 있다. 노출 여부를 가르는 축은 출처가 아니라
                러너 타입 계약(canEnableFastMode/canEnableEffort)뿐이다. */}
            <label
              className={cn(
                'label cursor-pointer justify-start gap-3 rounded-lg border border-base-300 px-3 py-2',
                !canEnableFastMode && 'opacity-60',
              )}
              title={canEnableFastMode ? t('fastModeHelp') : t('fastModeUnavailable')}
            >
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={fastModeSupported && canEnableFastMode}
                onChange={(event) => setFastModeSupported(event.target.checked)}
                disabled={!canEnableFastMode}
              />
              <span className="label-text">{t('fastModeSupported')}</span>
            </label>
            {canEnableEffort ? (
              <div className="rounded-lg border border-base-300 px-3 py-2">
                <p className="mb-2 text-xs font-medium text-[var(--color-fg-subtle)]">{t('supportedEffortLevels')}</p>
                <p className="mb-2 text-sm text-[var(--color-fg-muted)]">{t('supportedEffortLevelsHelp')}</p>
                <div className="flex flex-wrap gap-3">
                  {availableEffortLevels.map((level) => (
                    <label key={level} className="label cursor-pointer justify-start gap-2 py-0">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={supportedEffortLevels.includes(level)}
                        onChange={(event) =>
                          setSupportedEffortLevels((prev) =>
                            event.target.checked ? [...prev, level] : prev.filter((item) => item !== level),
                          )
                        }
                      />
                      <span className="label-text">{getEffortLevelLabel(level, tLayout)}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => setModalState(null)}
                disabled={isSaving}
              >
                {tCommon('actions.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={handleSave}
                disabled={isSaving || !label.trim() || !value.trim()}
              >
                {isSaving ? <Spinner /> : tCommon('actions.save')}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </BaseModal>
  );

  // 탐지 모델의 삭제는 종결이 아니다. 러너가 다음 주기에 같은 모델을 다시 보고해 승인 대기로 되살아나므로,
  // 되살아나지 않게 하려면 거절이어야 한다는 사실과 그 수단을 확인 단계에서 함께 준다.
  const isDetectedDelete = deleteTarget?.source === 'DETECTED';
  const deleteConfirmModal = (
    <DeleteConfirmModal
      isOpen={deleteTarget !== null}
      title={tCommon('actions.delete')}
      message={isDetectedDelete ? t('deleteDetectedConfirm') : t('deleteConfirm')}
      confirmLabel={tCommon('actions.delete')}
      cancelLabel={tCommon('actions.cancel')}
      isPending={deleteMutation.isPending}
      footerStart={
        isDetectedDelete ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={updateMutation.isPending || deleteMutation.isPending}
            onClick={() => {
              const target = deleteTarget;
              setDeleteTarget(null);
              if (target) void handleReject(target);
            }}
          >
            {t('rejectInstead')}
          </button>
        ) : null
      }
      onConfirm={() => void handleDelete()}
      onCancel={() => setDeleteTarget(null)}
    />
  );

  const selectedMachineLabel =
    machineOptions.find((option) => option.value === machineFilter)?.label ?? t('allRunners');
  const selectedRunnerLabel = runnerFilter === 'ALL' ? t('allModelTypes') : getRunnerTypeLabel(runnerFilter, tLayout);

  const bulkApprovalConfirmModal = (
    <BaseModal
      isOpen={bulkConfirmAction !== null}
      onClose={() => setBulkConfirmAction(null)}
      canClose={!bulkApprovalMutation.isPending}
      variant="bottomSheet"
      size="sm"
    >
      <h3 className="text-base font-semibold">
        {bulkConfirmAction === 'approve' ? t('bulkApproveTitle') : t('bulkRejectTitle')}
      </h3>
      <p className="mt-2 text-sm leading-6 text-[var(--color-fg-muted)]">
        {bulkConfirmAction === 'approve'
          ? t('bulkApproveConfirm', { count: selectedCount })
          : t('bulkRejectConfirm', { count: selectedCount })}
      </p>
      {isFilterSelection ? (
        <p className="mt-3 rounded-lg bg-base-200 px-3 py-2 text-xs leading-5 text-[var(--color-fg-muted)]">
          {t('bulkFilterContext', {
            runner: selectedMachineLabel,
            model: selectedRunnerLabel,
            search: search || t('noSearchFilter'),
          })}
        </p>
      ) : null}
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setBulkConfirmAction(null)}
          disabled={bulkApprovalMutation.isPending}
        >
          {tCommon('actions.cancel')}
        </button>
        <button
          type="button"
          className={cn('btn btn-sm', bulkConfirmAction === 'approve' ? 'btn-primary' : 'btn-error')}
          onClick={() => bulkConfirmAction && void handleBulkApproval(bulkConfirmAction)}
          disabled={bulkApprovalMutation.isPending}
          aria-label={bulkConfirmAction === 'approve' ? t('confirmBulkApprove') : t('confirmBulkReject')}
        >
          {bulkApprovalMutation.isPending ? <Spinner /> : bulkConfirmAction === 'approve' ? t('approve') : t('reject')}
        </button>
      </div>
    </BaseModal>
  );

  const detachedModals = isMounted
    ? createPortal(
        <>
          {editorModal}
          {deleteConfirmModal}
          {bulkApprovalConfirmModal}
        </>,
        document.body,
      )
    : null;

  const isActiveListLoading = activeList === 'approved' ? isApprovedLoading : isPendingLoading;
  const activeModels = activeList === 'approved' ? approvedModels : pendingModels;
  const activeMeta = activeList === 'approved' ? approvedMeta : pendingMeta;

  const pagination =
    activeMeta && activeMeta.totalPages > 1 ? (
      <PaginationControls
        summaryText={tCommon('pagination.pageInfo', {
          total: activeMeta.total,
          page: activeMeta.page,
          pages: Math.max(activeMeta.totalPages, 1),
        })}
        pageSize={activeList === 'approved' ? approvedPageSize : pendingPageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={(size) => {
          if (activeList === 'approved') {
            setApprovedPageSize(size);
            setApprovedPage(1);
          } else {
            setPendingPageSize(size);
            setPendingPage(1);
          }
        }}
        onPrev={() => {
          if (activeList === 'approved') setApprovedPage((page) => Math.max(page - 1, 1));
          else setPendingPage((page) => Math.max(page - 1, 1));
        }}
        onNext={() => {
          if (activeList === 'approved') setApprovedPage((page) => Math.min(page + 1, activeMeta.totalPages));
          else setPendingPage((page) => Math.min(page + 1, activeMeta.totalPages));
        }}
        disablePrev={activeMeta.page <= 1}
        disableNext={activeMeta.page >= activeMeta.totalPages}
        prevLabel={tCommon('actions.prev')}
        nextLabel={tCommon('actions.next')}
        getPageSizeLabel={(size) => tCommon('pagination.pageSizeOption', { size })}
      />
    ) : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div data-model-list-controls className="mb-3 flex shrink-0 flex-col gap-3 border-b border-base-300 pb-3">
        <div className="flex items-center justify-between gap-3">
          <div role="tablist" aria-label={t('registered')} className="flex min-w-0 gap-1 rounded-lg bg-base-200 p-1">
            <button
              type="button"
              role="tab"
              aria-selected={activeList === 'approved'}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                activeList === 'approved' ? 'bg-base-100 text-base-content shadow-sm' : 'text-[var(--color-fg-muted)]',
              )}
              onClick={() => setActiveList('approved')}
            >
              {t('registeredWithCount', { count: approvedMeta?.total ?? 0 })}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeList === 'pending'}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                activeList === 'pending'
                  ? 'bg-[var(--chip-warning-bg)] text-[var(--chip-warning-fg)] shadow-sm'
                  : 'text-[var(--color-fg-muted)]',
              )}
              onClick={() => setActiveList('pending')}
            >
              {t('pendingApprovalWithCount', { count: pendingMeta?.total ?? 0 })}
            </button>
          </div>
          <button type="button" className="btn btn-primary btn-sm shrink-0" onClick={openCreate}>
            {t('addModel')}
          </button>
        </div>

        <div className="grid gap-2 sm:grid-cols-[10rem_10rem_minmax(0,1fr)]">
          <Select size="sm" value={machineFilter} onChange={setMachineFilter} options={machineOptions} fullWidth />
          <Select
            size="sm"
            value={runnerFilter}
            onChange={(nextValue) => setRunnerFilter(nextValue as RunnerType | 'ALL')}
            options={[{ value: 'ALL', label: t('allModelTypes') }, ...runnerTypeOptions]}
            fullWidth
          />
          <input
            type="search"
            className="input input-sm input-bordered w-full"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder={t('searchPlaceholder')}
            aria-label={t('searchPlaceholder')}
          />
        </div>

        {activeList === 'pending' && selectedCount > 0 ? (
          <div className="rounded-lg bg-base-200 px-3 py-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-auto text-sm font-medium">{t('selectedCount', { count: selectedCount })}</span>
              <button
                type="button"
                className="btn btn-primary btn-xs"
                onClick={() =>
                  isFilterSelection ? setBulkConfirmAction('approve') : void handleBulkApproval('approve')
                }
                disabled={bulkApprovalMutation.isPending}
              >
                {t('bulkApprove')}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-xs text-error"
                onClick={() => setBulkConfirmAction('reject')}
                disabled={bulkApprovalMutation.isPending}
              >
                {t('bulkReject')}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={() => {
                  setSelectedIds([]);
                  setIsFilterSelection(false);
                }}
              >
                {t('clearSelection')}
              </button>
            </div>
            {isCurrentPageSelected && !isFilterSelection && (pendingMeta?.total ?? 0) > pendingModels.length ? (
              <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-base-300 pt-2 text-xs text-[var(--color-fg-subtle)]">
                <span>{t('currentPageSelected', { count: pendingModels.length })}</span>
                <button
                  type="button"
                  className="font-semibold text-primary underline-offset-2 hover:underline"
                  onClick={() => setIsFilterSelection(true)}
                >
                  {t('selectAllFiltered', { count: pendingMeta?.total ?? 0 })}
                </button>
              </div>
            ) : isFilterSelection ? (
              <p className="mt-2 border-t border-base-300 pt-2 text-xs font-medium text-primary">
                {t('allFilteredSelected', { count: selectedCount })}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div data-model-list-scroll className="min-h-0 flex-1 overflow-y-auto pr-1">
        {activeList === 'pending' && activeModels.length > 0 ? (
          <p className="mb-3 rounded-lg bg-warning/10 px-3 py-2 text-xs leading-5 text-[var(--chip-warning-fg)]">
            {t('pendingApprovalHint')}
          </p>
        ) : null}

        {isActiveListLoading ? (
          <div className="space-y-2">
            <div className="skeleton h-14 w-full" />
            <div className="skeleton h-14 w-full" />
          </div>
        ) : activeModels.length === 0 ? (
          <div className="rounded-lg border border-dashed border-base-300 p-6 text-center text-sm text-[var(--color-fg-muted)]">
            {search || runnerFilter !== 'ALL' || machineFilter !== 'ALL' ? t('noResults') : t('empty')}
          </div>
        ) : activeList === 'pending' ? (
          <div>
            <label className="mb-2 flex cursor-pointer items-center gap-2 px-1 text-xs font-medium text-[var(--color-fg-muted)]">
              <input
                ref={selectCurrentPageRef}
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={isCurrentPageSelected}
                onChange={toggleCurrentPage}
                aria-label={t('selectCurrentPage')}
              />
              <span>{t('selectCurrentPage')}</span>
            </label>
            <div className="overflow-hidden rounded-lg border border-base-300 bg-base-100">
              {pendingModels.map((model) => (
                <article key={model.id} className="border-b border-base-300 px-3 py-2.5 last:border-b-0">
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm shrink-0"
                      checked={selectedIdSet.has(model.id)}
                      onChange={() => toggleSelectedId(model.id)}
                      aria-label={t('selectModel', { label: model.label })}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <h4 className="min-w-0 max-w-full truncate text-sm font-semibold">
                          <Tooltip
                            content={model.label}
                            mode="overflowSafe"
                            showOnlyWhenTruncated
                            placement="top"
                            className="block min-w-0"
                          >
                            <span className="block truncate">{model.label}</span>
                          </Tooltip>
                        </h4>
                        <span className="shrink-0 rounded-full bg-[var(--chip-neutral-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--chip-neutral-fg)]">
                          {getRunnerTypeLabel(model.runnerType, tLayout)}
                        </span>
                        {/* 승인 전 편집으로 소유권을 가져간 행은 더 이상 자동 탐지 갱신 대상이 아니다. */}
                        {model.source === 'DETECTED' ? (
                          <span className="shrink-0 rounded-full bg-[var(--chip-info-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--chip-info-fg)]">
                            {t('detectedBadge')}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--color-fg-subtle)]">
                        <span className="truncate font-mono text-[var(--color-fg-subtle)]">{model.value}</span>
                        <span>{resolveMachineLabel(model, machineLabels)}</span>
                        <span className="inline-flex items-center gap-1">
                          <span>{t('maxInputTokens')}</span>
                          <span className="text-base-content/80">
                            {model.maxInputTokens != null ? model.maxInputTokens.toLocaleString() : t('notProvided')}
                          </span>
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button type="button" className="btn btn-ghost btn-xs" onClick={() => openEdit(model)}>
                        {tCommon('actions.edit')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-xs"
                        onClick={() => void handleApprove(model)}
                        disabled={updateMutation.isPending}
                      >
                        {t('approve')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs text-error"
                        onClick={() => void handleReject(model)}
                        disabled={updateMutation.isPending}
                      >
                        {t('reject')}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <div className="overflow-hidden rounded-lg border border-base-300 bg-base-100">
              {approvedModels.map((model) => (
                <article key={model.id} className="border-b border-base-300 px-3 py-2.5 last:border-b-0">
                  <div className="flex min-w-0 items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <h4 className="min-w-0 max-w-full truncate text-sm font-semibold">
                          <Tooltip
                            content={model.label}
                            mode="overflowSafe"
                            showOnlyWhenTruncated
                            placement="top"
                            className="block min-w-0"
                          >
                            <span className="block truncate">{model.label}</span>
                          </Tooltip>
                        </h4>
                        <span className="shrink-0 rounded-full bg-[var(--chip-neutral-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--chip-neutral-fg)]">
                          {getRunnerTypeLabel(model.runnerType, tLayout)}
                        </span>
                        {model.fastModeSupported ? (
                          <span className="shrink-0 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-semibold text-warning">
                            {t('fastModeBadge')}
                          </span>
                        ) : null}
                        {model.source === 'DETECTED' ? (
                          <span className="shrink-0 rounded-full bg-[var(--chip-info-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--chip-info-fg)]">
                            {t('detectedBadge')}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--color-fg-subtle)]">
                        <Tooltip
                          content={model.value}
                          mode="overflowSafe"
                          showOnlyWhenTruncated
                          placement="top"
                          className="block min-w-0 max-w-full"
                        >
                          <span className="block truncate font-mono text-base-content/80">{model.value}</span>
                        </Tooltip>
                        <span>{resolveMachineLabel(model, machineLabels)}</span>
                        <span className="inline-flex items-center gap-1">
                          <span>{t('sortOrder')}</span>
                          <span className="text-base-content/80">{model.sortOrder}</span>
                        </span>
                        {model.source === 'DETECTED' && model.detectedAt ? (
                          <span>{t('lastDetectedAt', { date: new Date(model.detectedAt).toLocaleString() })}</span>
                        ) : null}
                        <span className="inline-flex items-center gap-1">
                          <span>{t('maxInputTokens')}</span>
                          <span className="text-base-content/80">
                            {model.maxInputTokens != null ? model.maxInputTokens.toLocaleString() : t('noLimit')}
                          </span>
                        </span>
                      </div>
                    </div>
                    <ModelActionsMenu
                      ariaLabel={t('modelActions')}
                      editLabel={tCommon('actions.edit')}
                      deleteLabel={tCommon('actions.delete')}
                      deleteDisabled={deleteMutation.isPending}
                      onEdit={() => openEdit(model)}
                      onDelete={() => setDeleteTarget(model)}
                    />
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>

      {pagination ? (
        <div data-model-list-pagination className="shrink-0 border-t border-base-300 bg-base-100">
          {pagination}
        </div>
      ) : null}

      {detachedModals}
    </div>
  );
};
